import React, { memo, useContext } from 'react';
import { IconStockIncoming } from '@prisjakt/ui/icon/stock-incoming';
import { IconStockInStock } from '@prisjakt/ui/icon/stock-in-stock';
import { IconStockOutOfStock } from '@prisjakt/ui/icon/stock-out-of-stock';
import { IconStockUnknown } from '@prisjakt/ui/icon/stock-unknown';
import { AppContext } from 'src/components/app-context';
import * as S from './styles';
import type { StockStatusStates, StockStatusTranslationKey } from './types';
import type { TooltipPlacement } from '../tooltip-new';

type Props = {
  className?: string;
  size?: number;
  status?: StockStatusStates | null;
  statusText?: string;
  tooltip?: string | true;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  featuredOffer?: boolean;
};

export const StockStatus = memo((props: Props) => {
  const {
    className,
    size,
    status,
    statusText,
    tooltip,
    tooltipPlacement = 'left',
    featuredOffer = true,
  } = props;
  const { t } = useContext(AppContext);

  const renderIconComponent = (featured?: boolean) => {
    switch (status) {
      case 'in_stock_preorder':
      case 'incoming':
        return (
          <IconStockIncoming
            size={size}
            color={featured ? 'yellove700' : 'base400'}
          />
        );
      case 'in_stock':
        return (
          <IconStockInStock
            size={size}
            color={featured ? 'positive500' : 'base400'}
          />
        );
      case 'not_in_stock':
        return (
          <IconStockOutOfStock
            size={size}
            color={featured ? 'redio700' : 'base400'}
          />
        );
      default:
        return <IconStockUnknown size={size} color="base400" />;
    }
  };

  const getStatusText = (): string => {
    if (status === 'incoming' && statusText) {
      return statusText.replace(/-/g, '‑'); // Replace hyphen with hard hyphen to prevent row break in date (Eg in 2019-08-10).
    }

    return t(
      'product',
      `stock-status.${status || 'unknown'}` as StockStatusTranslationKey
    );
  };

  return (
    <S.Tooltip
      placement={tooltipPlacement as TooltipPlacement}
      title={tooltip === true ? getStatusText() : tooltip}
      className={className}
      renderOnMouseOver
    >
      <S.IconAndText>{renderIconComponent(featuredOffer)}</S.IconAndText>
    </S.Tooltip>
  );
});

export type StockStatusType = StockStatusStates;
